import { Button, Menu, MenuItem, Box, ButtonGroup } from '@mui/material';
import { AnyAction } from '@reduxjs/toolkit';
import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../../redux/hooks';
import { DateTime } from 'luxon';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

/**
 * QuarterPicker Component
 *
 * A custom date picker that allows users to select year and quarter combinations.
 * The picker opens with a year selection view, then navigates to quarter selection.
 * Returns the last date of the selected quarter (e.g., March 31 for Q1).
 *
 * @param dateFilterValueLocal - Current selected date value in 'yyyy-MM-dd' format
 * @param setDateFilter - Redux actions for managing date filter state
 * @param buttonSx - Custom styling for the trigger button
 * @param buttonColor - Color theme for the button
 * @param isQuarterlyForecast - Whether this is used for quarterly forecast selection
 * @param startDate - Earliest selectable date, defaults to new Date('2022-01-01')
 */
function QuarterPicker({
  dateFilterValueLocal,
  setDateFilter,
  buttonColor = 'primary',
  isQuarterlyForecast = false,
  startDate = new Date('2022-01-01'),
}: {
  dateFilterValueLocal: string | null;
  setDateFilter: {
    func: (value: string | null) => AnyAction;
    setGlobalFunc: () => AnyAction;
    clean: () => AnyAction;
  };
  buttonColor?: 'primary' | 'secondary';
  isQuarterlyForecast?: boolean;
  startDate?: Date;
}) {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  // State management for the picker UI
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [currentView, setCurrentView] = useState<'year' | 'quarter'>('year');
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const isOpen = Boolean(anchorEl);

  /**
   * Handles opening the menu and resetting to year view
   */
  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setCurrentView('year');
    setSelectedYear(null);
  }, []);

  /**
   * Handles closing the menu and resetting all state
   */
  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setCurrentView('year');
    setSelectedYear(null);
  }, []);

  /**
   * Handles date selection and dispatches Redux actions
   * Calculates the last date of the selected quarter
   *
   * @param year - Selected year
   * @param quarter - Selected quarter (1-4)
   */
  const onDateFilterChange = useCallback(
    (year: number, quarter: number) => {
      handleClose();

      // Calculate the last month of the quarter (March=3, June=6, Sept=9, Dec=12)
      const lastMonthOfQuarter = quarter * 3;
      // Get the last day of that month (e.g., March 31, June 30, etc.)
      const lastDateOfQuarter = DateTime.utc(year, lastMonthOfQuarter, 1).endOf(
        'month',
      );

      // Update Redux state with the calculated date
      dispatch(setDateFilter.func(lastDateOfQuarter.toFormat('yyyy-MM-dd')));
      dispatch(setDateFilter.setGlobalFunc());

      const newParams = new URLSearchParams(searchParams);
      newParams.set('quarter', String(year * 100 + quarter));
      setSearchParams(newParams, { replace: true });
    },
    [dispatch, setDateFilter, handleClose, searchParams, setSearchParams],
  );

  /**
   * Handles year selection from the year view
   * Transitions to quarter selection view
   */
  const handleYearSelect = useCallback((year: number) => {
    setSelectedYear(year);
    setCurrentView('quarter');
  }, []);

  /**
   * Handles quarter selection from the quarter view
   * Only proceeds if a year has been selected
   */
  const handleQuarterSelect = useCallback(
    (quarter: number) => {
      if (selectedYear) {
        onDateFilterChange(selectedYear, quarter);
      }
    },
    [selectedYear, onDateFilterChange],
  );

  /**
   * Generate list of available years starting from startDate year to next year
   */
  const years = useMemo(() => {
    const startDateTime = DateTime.fromJSDate(startDate);
    const startYear = startDateTime.isValid ? startDateTime.year : 2022;
    const currentYear = new Date().getFullYear();
    const yearsList = [];

    for (let year = startYear; year <= currentYear + 1; year++) {
      yearsList.push(year);
    }
    return yearsList;
  }, [startDate]);

  /**
   * Generate list of quarters based on selected year and startDate constraints
   */
  const quarters = useMemo(() => {
    const allQuarters = [
      { label: 'Q1 (Jan-Mar)', value: 1 },
      { label: 'Q2 (Apr-Jun)', value: 2 },
      { label: 'Q3 (Jul-Sep)', value: 3 },
      { label: 'Q4 (Oct-Dec)', value: 4 },
    ];

    // If no year is selected, return all quarters
    if (!selectedYear) return allQuarters;

    const startDateTime = DateTime.fromJSDate(startDate);
    if (!startDateTime.isValid) return allQuarters;

    const startYear = startDateTime.year;
    const startQuarter = Math.ceil(startDateTime.month / 3);

    // If selected year is the start year, filter out quarters before start quarter
    if (selectedYear === startYear) {
      return allQuarters.filter((quarter) => quarter.value >= startQuarter);
    }

    // For other years, return all quarters
    return allQuarters;
  }, [selectedYear, startDate]);

  /**
   * Parse the current selected date to determine which year/quarter to highlight
   */
  const selectedDate = useMemo(() => {
    if (dateFilterValueLocal) {
      try {
        const date = DateTime.fromFormat(dateFilterValueLocal, 'yyyy-MM-dd');
        if (date.isValid) {
          return {
            year: date.year,
            quarter: Math.ceil(date.month / 3), // Convert month to quarter
          };
        }
      } catch {
        // Ignore parsing errors and return null values
      }
    }
    return { year: null, quarter: null };
  }, [dateFilterValueLocal]);

  const isCurrentYear = useMemo(
    () =>
      dateFilterValueLocal &&
      DateTime.fromISO(dateFilterValueLocal).year === DateTime.local().year,
    [dateFilterValueLocal],
  );
  /**
   * Generates the appropriate label for quarterly forecast mode
   * Converts date value to "Q{number} {year} forecast" format
   */
  const quarterLabel = useMemo(() => {
    if (!dateFilterValueLocal) return 'All Q';
    try {
      const date = DateTime.fromISO(dateFilterValueLocal);
      if (!date.isValid) {
        return 'All Q';
      }
      const quarter = Math.ceil(date.month / 3); // Calculate quarter from month
      const year = date.year;
      if (year === new Date().getFullYear()) return `Q${quarter}`;

      return `Q${quarter} ${year}`;
    } catch {
      return 'All Q';
    }
  }, [dateFilterValueLocal]);

  const handleQuarterShift = useCallback(
    (direction: 'prev' | 'next') => {
      if (!dateFilterValueLocal) return;

      const currentDate = DateTime.fromFormat(
        dateFilterValueLocal,
        'yyyy-MM-dd',
      );
      if (!currentDate.isValid) return;

      const newDate =
        direction === 'next'
          ? currentDate.plus({ months: 3 }).endOf('month')
          : currentDate.minus({ months: 3 }).endOf('month');

      dispatch(setDateFilter.func(newDate.toFormat('yyyy-MM-dd')));
      dispatch(setDateFilter.setGlobalFunc());

      const newQuarter = Math.ceil(newDate.month / 3);
      const newParams = new URLSearchParams(searchParams);
      newParams.set('quarter', String(newDate.year * 100 + newQuarter));
      setSearchParams(newParams, { replace: true });
    },
    [
      dateFilterValueLocal,
      dispatch,
      setDateFilter,
      searchParams,
      setSearchParams,
    ],
  );

  return (
    <Box>
      <ButtonGroup
        variant="contained"
        color={buttonColor}
        size="small"
        sx={{ boxShadow: 'none', cursor: 'pointer' }}
      >
        <Button
          sx={{
            color: dateFilterValueLocal ? 'white' : 'black',
          }}
          color={buttonColor}
          onClick={() => handleQuarterShift('prev')}
        >
          <ChevronLeftIcon fontSize="medium" />
        </Button>

        <Button
          sx={{
            color: dateFilterValueLocal ? 'white' : 'black',
          }}
          color={buttonColor}
          onClick={handleClick}
        >
          {isQuarterlyForecast ? quarterLabel : 'Pick a date'}
        </Button>

        <Button
          sx={{
            color: dateFilterValueLocal ? 'white' : 'black',
          }}
          color={buttonColor}
          onClick={() => handleQuarterShift('next')}
        >
          <ChevronRightIcon fontSize="medium" />
        </Button>
      </ButtonGroup>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            maxHeight: 300,
            minWidth: isCurrentYear ? 129 : 166,
            width: isCurrentYear ? 129 : 166,
          },
        }}
      >
        {currentView === 'year'
          ? // Year Selection View
            years.map((year) => (
              <MenuItem
                key={year}
                onClick={() => handleYearSelect(year)}
                sx={{
                  justifyContent: 'flex-start',
                  backgroundColor:
                    year === selectedDate.year ? 'action.hover' : 'transparent',
                  color:
                    year === selectedDate.year
                      ? 'primary.main'
                      : 'text.primary',
                  '&:hover': {
                    backgroundColor:
                      year === selectedDate.year
                        ? 'primary.dark'
                        : 'action.hover',
                    color:
                      year === selectedDate.year
                        ? 'primary.contrastText'
                        : 'text.primary',
                  },
                }}
              >
                {year}
              </MenuItem>
            ))
          : // Quarter Selection View - filtered based on startDate
            quarters.map((quarter) => {
              const isSelected =
                selectedYear === selectedDate.year &&
                quarter.value === selectedDate.quarter;
              return (
                <MenuItem
                  key={quarter.value}
                  onClick={() => handleQuarterSelect(quarter.value)}
                  sx={{
                    justifyContent: 'flex-start',
                    backgroundColor: isSelected
                      ? 'action.hover'
                      : 'transparent',
                    color: isSelected ? 'primary.main' : 'text.primary',
                    '&:hover': {
                      backgroundColor: isSelected
                        ? 'primary.dark'
                        : 'action.hover',
                      color: isSelected
                        ? 'primary.contrastText'
                        : 'text.primary',
                    },
                  }}
                >
                  {quarter.label}
                </MenuItem>
              );
            })}
      </Menu>
    </Box>
  );
}

export default QuarterPicker;
